import {
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useCallback
} from "react";
import { ComboBoxDropDown } from "@etframework/ui.components";
import { useTaskTemplates } from "../../data/queries/taskTemplateQueries";
import { Trans, useTranslation } from "react-i18next";

export const TaskTemplateDropDown = forwardRef(
  ({ change, initialTaskType, persistentData, id }, ref) => {
    const { t, i18n } = useTranslation(["CreateTaskModal"]);
    const componentRef = useRef();
    const [taskTypeId, setTaskTypeId] = useState(initialTaskType);
    const { data: taskTemplateData } = useTaskTemplates(taskTypeId);
    const taskTemplateFields = {
      text: "Name",
      value: "Id"
    };

    const persistentDataValue = persistentData
      ? {
          Id: persistentData,
          Name: persistentData
        }
      : null;
    const value =
      taskTemplateData?.find?.((x) => x.Id === persistentData) ??
      persistentDataValue;

    const handleOnChange = (e) => {
      change(e);
    };

    const itemTemplate = useCallback((props) => {
      return (
        <div className="d-flex flex-column my-2">
          <span className="tax-return-form">{props?.Name}</span>
          <span className="tax-return-name">{props?.Description}</span>
        </div>
      );
    }, []);

    const setTaskType = useCallback((value) => {
      return setTaskTypeId(value);
    }, []);

    /* Built-in filtering only matches the text field (Name); templates are
       also searchable by their Description, which the dropdown displays.
       The Query passed to updateData must come from the ComboBox's own
       @syncfusion/ej2-data copy (the app pins an older major), so it is
       obtained through the component's getQuery() instead of an import. */
    const onFiltering = useCallback(
      (args) => {
        const combo = componentRef.current?.getComponentRef?.();
        const query = combo?.getQuery?.();
        const text = args.text?.toLowerCase() ?? "";

        const filtered = text
          ? taskTemplateData?.filter(
              (x) =>
                x.Name?.toLowerCase().includes(text) ||
                x.Description?.toLowerCase().includes(text)
            )
          : taskTemplateData;

        args.updateData(filtered, query);
      },
      [taskTemplateData]
    );

    useImperativeHandle(
      ref,
      () => ({
        getValue: () => componentRef.current.getValue(),
        setValue: (value) => componentRef.current.setValue(value),
        setTaskType,
        getComponentRef: () => componentRef.current
      }),
      [componentRef]
    );

    return (
      <ComboBoxDropDown
        key={taskTypeId}
        ref={componentRef}
        value={value}
        name="TaskName"
        dataSource={taskTemplateData}
        fields={taskTemplateFields}
        htmlFor={id}
        id={id}
        label={
          <Trans components={{ b: <span className="et-red" /> }}>
            {t("TaskNameLabel")}
          </Trans>
        }
        errorMessage={t("ErrorMsg")}
        locale={i18n?.language}
        change={handleOnChange}
        allowObjectBinding
        itemTemplate={itemTemplate}
        filterType="contains"
        allowFiltering
        filtering={onFiltering}
        showClearButton
        allowCustom={true}
      />
    );
  }
);
